import { DocumentCheckmarkRegular, DocumentDismissRegular, Info20Filled } from '@fluentui/react-icons';
import { TCustomFieldRaw } from '@modules/users/types/organizations.types';
import { ButtonLoading, ConfirmMenu } from '@shared/components';
import { TERMINOLOGY } from '@shared/i18n';
import { useSetCustomFieldDefaultsEnabled, useUpsertCustomFieldDefault } from '../../../api';
import { TDocument, TDraft, TVersion } from '../../../types';
import useIsVersionEditable from '../../../hooks/useIsVersionEditable.hook';
import CustomFieldInputs from './CustomFieldInputs';

type TProps = {
  doc: TDocument;
  versionId: string;
  version: TVersion | TDraft;
};

const TemplateDefaults = ({ doc, versionId, version }: TProps) => {
  const setEnabled = useSetCustomFieldDefaultsEnabled();
  const upsertDefault = useUpsertCustomFieldDefault();
  const isEditable = useIsVersionEditable(versionId, doc);
  const enabled = version.customFieldDefaults?.enabled ?? false;

  const saveValue = (fieldId: string, value: TCustomFieldRaw) =>
    upsertDefault.mutateAsync({ id: doc._id, fieldId, value });

  return (
    <div className="flex flex-col space-y-4">
      <div className="space-y-1">
        <p>
          {enabled
            ? `New documents created from this ${TERMINOLOGY.template.lower} start with these values instead of the ${TERMINOLOGY.template.lower}'s own.`
            : `Set the custom field values that new documents created from this ${TERMINOLOGY.template.lower} start with.`}
        </p>
        {enabled ? (
          <ConfirmMenu
            onConfirm={() => setEnabled.mutate({ id: doc._id, enabled: false })}
            warningText={`Disable ${TERMINOLOGY.template.lower} defaults? New documents get the ${TERMINOLOGY.template.lower}'s own values again. The defaults are kept.`}
          >
            <ButtonLoading
              icon={<DocumentDismissRegular />}
              loading={setEnabled.isPending}
              disabled={!isEditable || setEnabled.isPending}
            >
              Disable {TERMINOLOGY.template.lower} defaults
            </ButtonLoading>
          </ConfirmMenu>
        ) : (
          <ButtonLoading
            icon={<DocumentCheckmarkRegular />}
            loading={setEnabled.isPending}
            disabled={!isEditable}
            onClick={() => setEnabled.mutate({ id: doc._id, enabled: true })}
          >
            Enable {TERMINOLOGY.template.lower} defaults
          </ButtonLoading>
        )}
      </div>

      {enabled && (
        <CustomFieldInputs
          customFieldValues={version.customFieldDefaults?.values}
          fallbackToOrgDefault={false}
          isEditable={isEditable}
          saveValue={saveValue}
        />
      )}

      {!isEditable && (
        <div className="pt-2 w-full">
          <p className="flex items-center space-x-1">
            <Info20Filled />
            <span>
              You can only edit {TERMINOLOGY.template.lower} defaults for a draft or the latest unapproved version.
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default TemplateDefaults;
